<script setup lang="ts">
import { formatLocalDate, parseLocalDate, requireCurrencyCode, type Money } from '@taler/domain'
import { ActionButton, Dialog, StatusTag } from '@taler/ui'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import LoanTermsFields from '@/features/loans/components/LoanTermsFields.vue'
import type { Loan, LoanRateType, LoanType, UpdateLoanTermsInput } from '../internal/types.ts'

const props = defineProps<{ loan: Loan; isSubmitting: boolean; errorMessage: string | undefined }>()
const emit = defineEmits<{ save: [input: UpdateLoanTermsInput]; close: [] }>()

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')
const endDate = ref(props.loan.endDate === undefined ? '' : formatLocalDate(props.loan.endDate))
const lender = ref(props.loan.lender ?? '')
const rateType = ref<LoanRateType | undefined>(props.loan.rateType)
const loanType = ref<LoanType | undefined>(props.loan.loanType)
const monthlyPayment = ref<Money | null>(props.loan.monthlyPayment ?? null)
const loanCurrency = computed(() => requireCurrencyCode(props.loan.currency))

function submit(): void {
  const trimmedLender = lender.value.trim()
  emit('save', {
    endDate: endDate.value === '' ? undefined : parseLocalDate(endDate.value),
    lender: trimmedLender === '' ? undefined : trimmedLender,
    rateType: rateType.value,
    loanType: loanType.value,
    monthlyPayment: monthlyPayment.value ?? undefined,
  })
}
</script>

<template>
  <Dialog :open="true" :label="t('loans.editTermsTitle')" @close="emit('close')">
    <form class="flex w-96 flex-col gap-4 p-[var(--space-block)]" @submit.prevent="submit">
      <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
        {{ t('loans.editTermsTitle') }}
      </h2>

      <LoanTermsFields
        v-model:end-date="endDate"
        v-model:lender="lender"
        v-model:rate-type="rateType"
        v-model:loan-type="loanType"
        v-model:monthly-payment="monthlyPayment"
        :currency="loanCurrency"
        :locale="currentLocale"
      />

      <StatusTag v-if="errorMessage" tone="danger">{{ errorMessage }}</StatusTag>

      <div class="flex gap-2">
        <ActionButton type="submit" :disabled="isSubmitting">
          {{ isSubmitting ? t('loans.saving') : t('loans.save') }}
        </ActionButton>
        <ActionButton type="button" variant="secondary" @click="emit('close')">
          {{ t('loans.cancel') }}
        </ActionButton>
      </div>
    </form>
  </Dialog>
</template>
